import { makeAutoObservable, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import { ProjectFile, Breadcrumb } from "../api/ProjectFilesApiClient";

class ProjectFilesStore {
  isLoading: boolean = false;
  files: ProjectFile[] = [];
  currentFolderId: number | null = null;
  breadcrumbs: Breadcrumb[] = [];
  draggedItem: ProjectFile | null = null;
  selectedItems: Set<number> = new Set();
  error: string | null = null;
  
  constructor(private apiStore: ApiStore) {
    makeAutoObservable(this);
  }

  setLoading = (loading: boolean): void => {
    this.isLoading = loading;
  }

  setError = (error: string | null): void => {
    this.error = error;
  }

  loadFiles = async (projectId: string, folderId: number | null = null): Promise<void> => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      console.log(`Loading files for project ${projectId}, folder ID: ${folderId}`);
      
      const [files, breadcrumbs] = await Promise.all([
        this.apiStore.ProjectFilesApiClient.getFilesInFolder(projectId, folderId),
        this.apiStore.ProjectFilesApiClient.getBreadcrumbs(projectId, folderId)
      ]);
      
      runInAction(() => {
        this.files = files;
        this.currentFolderId = folderId;
        this.breadcrumbs = breadcrumbs;
      });
    } catch (error) {
      console.error('Error loading files:', error);
      runInAction(() => {
        this.setError('Failed to load files. Please try again.');
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  navigateToFolder = async (projectId: string, folder: ProjectFile): Promise<void> => {
    if (!folder.isFolder) return;
    await this.loadFiles(projectId, folder.id);
  }

  navigateToBreadcrumb = async (projectId: string, index: number): Promise<void> => {
    if (index < 0 || index >= this.breadcrumbs.length) {
      await this.loadFiles(projectId, null);
      return;
    }
    
    const breadcrumb = this.breadcrumbs[index];
    await this.loadFiles(projectId, breadcrumb.folderId);
  }

  createFolder = async (projectId: string, folderName: string): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);
    
    if (!folderName.trim()) {
      runInAction(() => {
        this.setError('Please enter a folder name.');
        this.setLoading(false);
      });
      return false;
    }
    
    try {
      console.log(`Creating folder "${folderName}" in project ${projectId}, parent: ${this.currentFolderId}`);
      const folder = await this.apiStore.ProjectFilesApiClient.createFolder(
        projectId,
        folderName.trim(),
        this.currentFolderId
      );
      
      if (folder) {
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      runInAction(() => {
        this.setError('Failed to create folder. Please try again.');
      });
      return false;
    } catch (error) {
      console.error('Error creating folder:', error);
      runInAction(() => {
        this.setError('Failed to create folder. Please try again.');
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  uploadFile = async (projectId: string, file: File): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);
    
    if (!file) {
      runInAction(() => {
        this.setError('No file selected.');
        this.setLoading(false);
      });
      return false;
    }
    
    try {
      console.log(`Uploading file ${file.name} to project ${projectId}, folder: ${this.currentFolderId}`);
      const uploadedFile = await this.apiStore.ProjectFilesApiClient.uploadFile(
        projectId,
        file,
        this.currentFolderId
      );
      
      if (uploadedFile) {
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      runInAction(() => {
        this.setError('Failed to upload file. Please try again.');
      });
      return false;
    } catch (error) {
      console.error('Error uploading file:', error);
      runInAction(() => {
        this.setError('Failed to upload file. Please try again.');
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  moveFile = async (projectId: string, fileId: number, targetFolderId: number | null): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      const success = await this.apiStore.ProjectFilesApiClient.moveFile(fileId, targetFolderId);
      
      if (success) {
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      runInAction(() => {
        this.setError('Failed to move file.');
      });
      return false;
    } catch (error) {
      console.error('Error moving file:', error);
      runInAction(() => {
        this.setError('Failed to move file.');
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  moveFolder = async (projectId: string, folderId: number, targetFolderId: number | null): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      const success = await this.apiStore.ProjectFilesApiClient.moveFolder(folderId, targetFolderId);
      
      if (success) {
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      runInAction(() => {
        this.setError('Failed to move folder. It may create a circular reference.');
      });
      return false;
    } catch (error) {
      console.error('Error moving folder:', error);
      runInAction(() => {
        this.setError('Failed to move folder.');
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  batchMove = async (projectId: string, targetFolderId: number | null): Promise<boolean> => {
    if (this.selectedItems.size === 0) return false;
    
    this.setLoading(true);
    this.setError(null);
    
    const fileIds: number[] = [];
    const folderIds: number[] = [];
    
    this.selectedItems.forEach(id => {
      const item = this.files.find(f => f.id === id);
      if (item) {
        if (item.isFolder) {
          folderIds.push(id);
        } else {
          fileIds.push(id);
        }
      }
    });
    
    try {
      const result = await this.apiStore.ProjectFilesApiClient.batchMove(fileIds, folderIds, targetFolderId);
      
      if (result) {
        this.selectedItems.clear();
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error batch moving items:', error);
      runInAction(() => {
        this.setError('Failed to move selected items.');
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  renameItem = async (projectId: string, item: ProjectFile, newName: string): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      const success = item.isFolder 
        ? await this.apiStore.ProjectFilesApiClient.renameFolder(item.id, newName)
        : await this.apiStore.ProjectFilesApiClient.renameFile(item.id, newName);
      
      if (success) {
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      runInAction(() => {
        this.setError(`Failed to rename ${item.isFolder ? 'folder' : 'file'}.`);
      });
      return false;
    } catch (error) {
      console.error('Error renaming item:', error);
      runInAction(() => {
        this.setError(`Failed to rename ${item.isFolder ? 'folder' : 'file'}.`);
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  deleteItem = async (projectId: string, item: ProjectFile, forceDelete: boolean = false): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      console.log(`Deleting ${item.isFolder ? 'folder' : 'file'}: ${item.fileName} with ID: ${item.id}`);
      
      const success = item.isFolder 
        ? await this.apiStore.ProjectFilesApiClient.deleteFolder(item.id, forceDelete)
        : await this.apiStore.ProjectFilesApiClient.deleteFile(item.id);
      
      if (success) {
        await this.loadFiles(projectId, this.currentFolderId);
        return true;
      }
      
      runInAction(() => {
        this.setError(`Failed to delete ${item.isFolder ? 'folder' : 'file'}.`);
      });
      return false;
    } catch (error) {
      console.error('Error deleting item:', error);
      runInAction(() => {
        this.setError(`Failed to delete ${item.isFolder ? 'folder' : 'file'}.`);
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  toggleItemSelection = (itemId: number): void => {
    if (this.selectedItems.has(itemId)) {
      this.selectedItems.delete(itemId);
    } else {
      this.selectedItems.add(itemId);
    }
  }

  clearSelection = (): void => {
    this.selectedItems.clear();
  }

  startDrag = (item: ProjectFile): void => {
    this.draggedItem = item;
  }

  endDrag = (): void => {
    this.draggedItem = null;
  }

  handleDrop = async (projectId: string, targetItem: ProjectFile): Promise<boolean> => {
    if (!this.draggedItem || this.draggedItem.id === targetItem.id) {
      return false;
    }
    
    if (!targetItem.isFolder) {
      return false;
    }
    
    const success = this.draggedItem.isFolder
      ? await this.moveFolder(projectId, this.draggedItem.id, targetItem.id)
      : await this.moveFile(projectId, this.draggedItem.id, targetItem.id);
    
    this.draggedItem = null;
    return success;
  }

  clearStore = (): void => {
    this.files = [];
    this.currentFolderId = null;
    this.breadcrumbs = [];
    this.error = null;
    this.draggedItem = null;
    this.selectedItems.clear();
  }
}

export default ProjectFilesStore;